import { useContext, useEffect, useState } from "react";
import Footer from "../components/Footer";
import Head from "./Head";
import GlobalConfig from "../context/GlobalConfig";
import QRCode from "../components/QRCode";
import buttonLinkImage from "../assets/buttonLink.svg";
import { languageData } from "../constants/languageData";
import { trackAction } from "../utils/hyperdx";
import PhoneSession from "./PhoneSession";
import JourneyConfig from "../context/JourneyConfig";
import { useParams } from "react-router-dom";

function ContinueOnPhone({
  stepperFunc,
  stepHistory,
  steps,
  currentDocumentStep,
  currentDocumentRetries,
  currentStep,
}) {
  const { config, routesAndNavigation } = useContext(GlobalConfig);
  const { journeyId } = useParams();
  const [startedJourneyInMobile, setStartedJourneyInMobile] = useState(false);
  const { journey, getJourney } = useContext(JourneyConfig);
  const { branding } = config ?? {};
  const { routes } = routesAndNavigation ?? {};
  const language = sessionStorage.getItem("language") ?? "en";
  const sessionId = sessionStorage.getItem("sessionId");
  const color = config?.branding?.accentColor;
  const [width, setWidth] = useState(window.innerWidth);
  const copiedText =
    languageData[language]?.continueOnPhone?.copiedButton ??
    languageData["en"]?.continueOnPhone?.copiedButton ??
    "Copied!";
  const [buttonText, setButtonText] = useState(
    languageData[language]?.continueOnPhone?.button
  );
  const restrictedPages = ["enable-mobile-flow", "error", "expired", "faq"];

  const validStep = () => {
    for (let i = stepHistory.length - 1; i >= 0; i--) {
      const step = stepHistory[i];
      const route = routes[step];
      if (!route) continue; // Skip if route is undefined
      const isRestricted = restrictedPages.some((keyword) =>
        route.includes(keyword)
      );
      if (!isRestricted) {
        return step; // Return the first valid step found
      }
    }
    return null; // Return null if no valid step is found
  };

  const bgColor =
    "color-mix(in srgb, " + config?.branding?.accentColor + " 10%, white)";
  const url = window.location.href;
  const baseUrl = !url?.includes('test') ? new URL(url).origin : '192.168.0.112';

  // Carry the currently selected language to the mobile session (if one is set),
  // so the phone journey opens in the same language as the desktop flow.
  const storedLanguage = sessionStorage.getItem("language");
  const languageParam = storedLanguage ? `&language=${storedLanguage}` : "";
  const mobileLink = `${baseUrl}/${journeyId}?mobile=true&sessionId=${sessionId}&currentStep=${validStep()}&currentDocumentStep=${currentDocumentStep}&currentDocumentRetries=${currentDocumentRetries}${languageParam}`;

  useEffect(() => {
    if (journeyId && !journey) getJourney(journeyId);
  }, [journeyId]);

  useEffect(() => {
    if (startedJourneyInMobile || !journeyId || sessionStorage.getItem('nonImportantStep')) {
      return; // No cleanup needed when we early return
    }

    const eventSource = new EventSource(
      `/otk-service/get-journey-status/${journeyId}`
    );

    const handleMessage = (e) => {
      try {
        const res = JSON.parse(e?.data);
        if (res?.mobileStatus) {
          setStartedJourneyInMobile(true);
          if (eventSource && typeof eventSource.close === 'function') {
            eventSource.close();
          }
        }
      } catch (error) {
        console.error("Error parsing SSE data:", error);
      }
    };

    const handleError = () => {
      if (eventSource && typeof eventSource.close === 'function') {
        eventSource.close();
      }
    };

    eventSource.onmessage = handleMessage;
    eventSource.onerror = handleError;

    // Only return cleanup function when eventSource was actually created
    return () => {
      if (eventSource && typeof eventSource.close === 'function') {
        eventSource.close();
      }
    };
  }, [journeyId, startedJourneyInMobile]);

  const copyLink = async () => {
    const linkToCopy = mobileLink;
    trackAction("journey:switch_to_mobile", { method: "copy_link" });

    try {
      // Try modern navigator.clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(linkToCopy);
        setButtonText(copiedText);
      } else {
        throw new Error('Clipboard API not available');
      }
    } catch (err) {
      console.log('Navigator clipboard failed, using fallback method:', err);
      
      // Fallback method using document.execCommand
      const textArea = document.createElement('textarea');
      textArea.value = linkToCopy;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      textArea.style.opacity = '0';
      textArea.setAttribute('readonly', '');
      document.body.appendChild(textArea);
      
      // Focus and select the text
      textArea.focus();
      textArea.select();
      textArea.setSelectionRange(0, 99999); // For mobile devices
      
      try {
        const successful = document.execCommand('copy');
        if (successful) {
          setButtonText(copiedText);
        } else {
          // If copy fails, select the input field text for manual copying
          const inputElement = document.querySelector('input[type="text"]');
          if (inputElement) {
            inputElement.focus();
            inputElement.select();
            inputElement.setSelectionRange(0, 99999); // For mobile devices
          }
          setButtonText(copiedText);
        }
      } catch (fallbackErr) {
        console.error('Fallback copy failed:', fallbackErr);
        // Select the input field text for manual copying
        const inputElement = document.querySelector('input[type="text"]');
        if (inputElement) {
          inputElement.focus();
          inputElement.select();
          inputElement.setSelectionRange(0, 99999); // For mobile devices
        }
        setButtonText(copiedText);
      } finally {
        document.body.removeChild(textArea);
      }
    }
    
    setTimeout(() => {
      setButtonText(languageData[language]?.continueOnPhone?.button);
    }, 3000); // Revert back to "Copy" after 3 seconds
  };

  return (
    <>
      {!startedJourneyInMobile ? (
        <>
          <Head
            showBackIcon
            handleBack={() => stepperFunc(stepHistory[stepHistory.length - 1])}
            stepperFunc={stepperFunc}
            showLanguages={false}
            showMobileFlow={false}
            showMobileText
          />
          <div
            className="parent-container"
            style={{ paddingTop: width < 768 ? 64 : 0 }}
          >
            <div className="main">
              <div className="left-section">
                <QRCode
                  link={mobileLink}
                />
              </div>
              <div
                className="right-section md:items-start items-center"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  fontFamily: "Inter, sans-serif",
                }}
              >
                <div className="flex flex-col">
                  <div className="text-[28px] font-semibold text-[#000000]">
                    {languageData[language]?.continueOnPhone?.heading}
                  </div>
                  <div className="mt-3 text-[16px] font-semibold text-[#656565]">
                    {
                      languageData[language]?.continueOnPhone
                        ?.subheading
                    }
                  </div>
                  <div className="flex flex-col mt-16">
                    <div className="flex flex-col gap-4">
                      <div className="flex flex-row gap-3 items-center">
                        <div>
                          <div
                            className={`px-[8px] py-[8px] rounded-full`}
                            style={{ backgroundColor: bgColor }}
                          >
                            {/* <img src={link_img} alt='link_img' /> */}
                            <svg
                              width="28"
                              height="29"
                              viewBox="0 0 28 29"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <circle
                                cx="14"
                                cy="14.5996"
                                r="14"
                                fill={bgColor}
                                style={{ fill: bgColor }}
                              />
                              <g clip-path="url(#clip0_2274_22206)">
                                <path
                                  d="M17.333 11.2666H14.6663V12.5999H17.333C18.433 12.5999 19.333 13.4999 19.333 14.5999C19.333 15.6999 18.433 16.5999 17.333 16.5999H14.6663V17.9333H17.333C19.173 17.9333 20.6663 16.4399 20.6663 14.5999C20.6663 12.7599 19.173 11.2666 17.333 11.2666ZM13.333 16.5999H10.6663C9.56634 16.5999 8.66634 15.6999 8.66634 14.5999C8.66634 13.4999 9.56634 12.5999 10.6663 12.5999H13.333V11.2666H10.6663C8.82634 11.2666 7.33301 12.7599 7.33301 14.5999C7.33301 16.4399 8.82634 17.9333 10.6663 17.9333H13.333V16.5999ZM11.333 13.9333H16.6663V15.2666H11.333V13.9333Z"
                                  fill="#1658aa"
                                  style={{ fill: color }}
                                />
                              </g>
                              <defs>
                                <clipPath id="clip0_2274_22206">
                                  <rect
                                    width="16"
                                    height="16"
                                    fill="white"
                                    transform="translate(6 6.59961)"
                                  />
                                </clipPath>
                              </defs>
                            </svg>
                          </div>
                        </div>
                        <div className="text-[16px] text-[#656565]">
                          {
                            languageData[language]?.continueOnPhone
                              ?.subheading2
                          }
                        </div>
                      </div>
                      <div className="flex flex-row gap-3 items-center">
                        <div>
                          <div
                            className={`px-[8px] py-[8px] rounded-full`}
                            style={{ backgroundColor: bgColor }}
                          >
                            <svg
                              width="28"
                              height="29"
                              viewBox="0 0 28 29"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <circle
                                cx="14"
                                cy="14.5996"
                                r="14"
                                fill={bgColor}
                                style={{ fill: bgColor }}
                              />
                              <g clip-path="url(#clip0_2274_13650)">
                                <path
                                  d="M19.333 9.93262H8.66634C7.93301 9.93262 7.33301 10.5326 7.33301 11.266V17.9326C7.33301 18.666 7.93301 19.266 8.66634 19.266H19.333C20.0663 19.266 20.6663 18.666 20.6663 17.9326V11.266C20.6663 10.5326 20.0663 9.93262 19.333 9.93262ZM19.333 13.9326H15.333V11.266H19.333V13.9326ZM8.66634 11.266H13.9997V17.9326H8.66634V11.266ZM15.333 17.9326V15.266H19.333V17.9326H15.333ZM12.333 14.5993C12.333 15.1526 11.8863 15.5993 11.333 15.5993C10.7797 15.5993 10.333 15.1526 10.333 14.5993C10.333 14.046 10.7797 13.5993 11.333 13.5993C11.8863 13.5993 12.333 14.046 12.333 14.5993Z"
                                  fill="#1658aa"
                                  style={{ fill: color }}
                                />
                              </g>
                              <defs>
                                <clipPath id="clip0_2274_13650">
                                  <rect
                                    width="16"
                                    height="16"
                                    fill="white"
                                    transform="translate(6 6.59961)"
                                  />
                                </clipPath>
                              </defs>
                            </svg>
                          </div>
                        </div>
                        <div className="text-[16px] text-[#656565]">
                          {
                            languageData[language]?.continueOnPhone
                              ?.text
                          }
                        </div>
                      </div>
                      <div className="flex flex-row gap-3 items-center">
                        <div>
                          <div
                            className={`px-[8px] py-[8px] rounded-full`}
                            style={{ backgroundColor: bgColor }}
                          >
                            <svg
                              width="28"
                              height="29"
                              viewBox="0 0 28 29"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <circle
                                cx="14"
                                cy="14.5996"
                                r="14"
                                fill={bgColor}
                                style={{ fill: bgColor }}
                              />
                              <g clip-path="url(#clip0_2274_22250)">
                                <path
                                  d="M11.9999 17.3997L9.19993 14.5997L8.2666 15.533L11.9999 19.2663L19.9999 11.2663L19.0666 10.333L11.9999 17.3997Z"
                                  fill="#1658aa"
                                  style={{ fill: color }}
                                />
                              </g>
                              <defs>
                                <clipPath id="clip0_2274_22250">
                                  <rect
                                    width="16"
                                    height="16"
                                    fill="white"
                                    transform="translate(6 6.59961)"
                                  />
                                </clipPath>
                              </defs>
                            </svg>
                          </div>
                        </div>
                        <div className="text-[16px] text-[#656565]">
                          {
                            languageData[language]?.continueOnPhone
                              ?.text1
                          }
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="w-full">
                  <div
                    className="relative h-12 mt-16 w-full min-w-[200px]"
                    onClick={copyLink}
                  >
                    <div
                      className="absolute top-2/4 flex justify-center items-center h-5 -translate-y-2/4 place-items-center text-blue-gray-500"
                      style={{ right: "0.1rem" }}
                    >
                      <button
                        style={{
                          backgroundColor: color ?? "#1658aa",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center", // Center the text
                          width: "6rem",
                          borderRadius: "5px",
                          padding: "10px 20px",
                          color: "white",
                        }}
                      >
                        {buttonText}
                        {buttonText !== copiedText && (
                          <img
                            style={{ marginLeft: "0.4rem" }}
                            src={buttonLinkImage}
                            alt="button_link"
                          />
                        )}
                      </button>
                    </div>
                    <input
                      readOnly
                      disabled
                      value={mobileLink}
                      type="text"
                      className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 h-12 cursor-text"
                      onClick={(e) => {
                        e.target.select();
                        e.target.setSelectionRange(0, 99999);
                      }}
                      onFocus={(e) => {
                        e.target.select();
                        e.target.setSelectionRange(0, 99999);
                      }}
                    />
                  </div>
                </div>
                <Footer />
              </div>
            </div>
          </div>
        </>
      ) : (
        <PhoneSession stepperFunc={stepperFunc} stepHistory={stepHistory} />
      )}
    </>
  );
}

export default ContinueOnPhone;
